import {
  alpha,
  Slider as MuiSlider,
  SliderProps as MuiSliderProps,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  SLIDER_SIZES,
  SliderSize,
  SliderVariant,
} from '../../../config/slider';

interface StyledSliderProps extends Omit<MuiSliderProps, 'color'> {
  $size?: SliderSize;
  $variant?: SliderVariant;
}

export const StyledSlider = styled(MuiSlider)<StyledSliderProps>(({
  theme,
  $size = SliderSize.MEDIUM,
  $variant = SliderVariant.PRIMARY,
  disabled,
}) => {
  const sizeConfig = SLIDER_SIZES[$size];
  const trackHeight = theme.spacing(sizeConfig.trackHeight);
  const thumbSize = theme.spacing(sizeConfig.thumbSize);
  const thumbHoverSize = theme.spacing(sizeConfig.thumbHoverSize);

  const variantColor = disabled
    ? theme.palette.text.fourth
    : $variant === SliderVariant.PRIMARY
      ? theme.palette.primary.main
      : theme.palette.secondaryInvert.main;
  const inactiveTrackColor = alpha(variantColor, 0.4);
  const disabledColor = theme.palette.text.fourth;

  return {
    color: variantColor,
    height: trackHeight,

    // Track (active portion)
    '& .MuiSlider-track': {
      border: 'none',
      backgroundColor: variantColor,
      ...(disabled && {
        backgroundColor: disabledColor,
      }),
    },

    // Rail (inactive portion)
    '& .MuiSlider-rail': {
      height: trackHeight,
      backgroundColor: inactiveTrackColor,
    },

    // Thumb
    '& .MuiSlider-thumb': {
      width: thumbSize,
      height: thumbSize,
      backgroundColor: variantColor,

      '&:hover, &.Mui-focusVisible': {
        boxShadow: `0 0 0 ${thumbHoverSize} ${alpha(variantColor, 0.16)}`,
      },

      ...(disabled && {
        backgroundColor: disabledColor,
        borderColor: disabledColor,
        boxShadow: 'none',
      }),
    },

    // Marks
    '& .MuiSlider-mark': {
      width: theme.spacing(0.5),
      height: theme.spacing(0.5),
      borderRadius: theme.customShape.borderRadiusFull,
      backgroundColor: variantColor,
      ...(disabled && {
        backgroundColor: disabledColor,
      }),
    },

    '& .MuiSlider-markActive': {
      backgroundColor: theme.palette.white,
    },

    // Mark labels
    '& .MuiSlider-markLabel': {
      color: theme.palette.text.secondary,
      ...(disabled && {
        color: disabledColor,
      }),
    },
  };
});
